import { getCurrentApi, setDefaultApiUrl, initializeDefaultApi } from './api';
import { Hotel, CreateHotelRequest } from '../models/Hotel';
import * as firebaseService from './firebaseService';
import { auditService } from './auditService';

export const getHotels = async (): Promise<Hotel[]> => {
  const api = getCurrentApi();
  const response = await api.get<Hotel[]>('/hotels');
  return response.data;
};

/**
 * Get hotels directly from Firebase without admin dependencies
 */
export const getPublicHotelsList = async (): Promise<Hotel[]> => {
  try {
    console.log("Getting hotels from Firebase...");
    
    // Get all hotels from Firebase
    const firebaseHotels = await firebaseService.getFirebaseHotels();
    console.log("Firebase hotels found:", firebaseHotels.length);
    
    if (firebaseHotels.length > 0) {
      // Convert Firebase hotels to Hotel format
      return firebaseHotels.map(fbHotel => firebaseService.convertToBackendHotel(fbHotel) as Hotel);
    } else {
      console.error("No hotels found in Firebase.");
      throw new Error("No hotels configured. Please add hotels to Firebase.");
    }
  } catch (error) {
    console.error("Error getting hotels from Firebase:", error);
    throw new Error("Could not load hotels from Firebase.");
  }
};

export const getHotelById = async (id: number): Promise<Hotel> => {
  const api = getCurrentApi();
  const response = await api.get<Hotel>(`/hotels/${id}`);
  return response.data;
};

export const createHotel = async (hotel: CreateHotelRequest): Promise<Hotel> => {
  // Make sure the hotel object has all the required fields
  const hotelData = {
    name: hotel.name || '',
    address: hotel.address || '',
    serverUrl: hotel.serverUrl || '',
    serverPort: hotel.serverPort
  };
  
  const api = getCurrentApi();
  const response = await api.post<Hotel>('/hotels', hotelData);
  
  // Log the action to Firebase
  await auditService.logAction('Hotel Created', {
    additionalInfo: `Created hotel: ${response.data.name} at ${response.data.serverUrl}${response.data.serverPort ? ':' + response.data.serverPort : ''}`
  });
  
  return response.data;
};

export const deleteHotel = async (id: number): Promise<void> => {
  // Get hotel info before deleting for audit log
  const hotel = await getHotelById(id);
  
  const api = getCurrentApi();
  await api.delete(`/hotels/${id}`);
  
  // Log the action to Firebase
  await auditService.logAction('Hotel Deleted', {
    additionalInfo: `Deleted hotel: ${hotel.name} (ID: ${id})`
  });
};

/**
 * Find hotel by hotel code from all available hotels
 */
export const findHotelByCode = async (hotelCode: string): Promise<Hotel | null> => {
  try {
    console.log(`Searching for hotel with code: ${hotelCode}`);
    
    // Get all hotels from public list
    const hotels = await getPublicHotelsList();
    
    // Find hotel with matching code
    const hotel = hotels.find(h => h.hotelCode === hotelCode);
    
    if (hotel) {
      console.log(`Found hotel: ${hotel.name} (ID: ${hotel.id})`);
      return hotel;
    } else {
      console.log(`No hotel found with code: ${hotelCode}`);
      return null;
    }
  } catch (error) {
    console.error('Error finding hotel by code:', error);
    return null;
  }
};